import SportIcon from '@/components/SportIcon';
/**
 * MemberProfilePage.jsx
 * Personal profile for any logged-in member:
 *   - Profile card (editable: name, photo, DOB)
 *   - Team & teammates
 *   - Sport stats
 *   - Upcoming matches
 *   - Recent match history
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  User, Edit2, Check, X, Camera, Trophy, CalendarDays,
  Swords, Target, TrendingUp, Clock, CheckCircle2, Star,
  Users, ChevronRight, RefreshCw, Share2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const SPORT_ICONS = {
  table_tennis: '🏓', badminton: '🏸', chess: '♟️',
  carrom: '🎯', pickleball: '🥒', squash: '🎾',
  cricket: '🏏', football: '⚽', default: '🏅',
};
const SPORT_LABELS = {
  table_tennis: 'Table Tennis', badminton: 'Badminton', chess: 'Chess',
  carrom: 'Carrom', pickleball: 'Pickleball', squash: 'Squash',
  cricket: 'Cricket', football: 'Football',
};
const ROLE_LABELS = { captain: 'Captain', vice_captain: 'Vice Captain', player: '' };

const fmtDate = (d) => d ? new Date(d + 'T00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) : 'TBD';

export default function MemberProfilePage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [rankingsEntry, setRankingsEntry] = useState(null); // this player's entry from rankings
  const [showCard, setShowCard] = useState(false); // player card modal

  // Edit state
  const [editing, setEditing]   = useState(false);
  const [editName, setEditName] = useState('');
  const [editDob,  setEditDob]  = useState('');
  const [editPhoto, setEditPhoto] = useState(null); // base64
  const [saving, setSaving]     = useState(false);
  const photoRef = useRef(null);

  const fetchProfile = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`/api/orgs/${orgSlug}/me/member-profile`,
        { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) setProfile(await res.json());
    } catch (_) { if (!silent) toast.error('Failed to load profile'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchProfile(); }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch rankings and find this player's entry — guaranteed to match rankings page
  useEffect(() => {
    const playerId = profile?.player?.id;
    const displayName = (profile?.user?.display_name || user?.display_name || '').toLowerCase();
    if (!playerId && !displayName) return;
    fetch(`/api/orgs/${orgSlug}/rankings`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.rankings) return;
        // Match by player_id first, then by name
        const entry = data.rankings.find(p => p.player_id === playerId)
          || data.rankings.find(p => (p.player_name || '').toLowerCase() === displayName);
        if (entry) setRankingsEntry(entry);
      })
      .catch(() => {});
  }, [profile?.player?.id, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setEditName(profile?.user?.display_name || '');
    setEditDob(profile?.player?.dob || '');
    setEditPhoto(null);
    setEditing(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Photo must be under 3 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setEditPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { display_name: editName };
      if (editDob) body.dob = editDob;
      if (editPhoto) body.photo_url = editPhoto;
      const res = await fetch(`/api/orgs/${orgSlug}/me/member-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Profile updated!');
        setEditing(false);
        await fetchProfile(true);
        // Update auth store display name
        useAuthStore.setState(s => ({
          user: s.user ? { ...s.user, display_name: editName || s.user.display_name } : s.user
        }));
      } else { toast.error('Failed to save'); }
    } catch (_) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // ── Stats: use rankings entry (same source as rankings page) ────────────
  const sportStats = React.useMemo(() => {
    if (rankingsEntry) {
      return {
        played:  rankingsEntry.played  || 0,
        wins:    rankingsEntry.wins    || 0,
        losses:  rankingsEntry.losses  || 0,
        draws:   0,
        win_pct: rankingsEntry.win_pct || 0,
      };
    }
    // Fallback: compute from recent_matches
    const rm = profile?.recent_matches || [];
    if (!rm.length) return profile?.stats || {};
    const wins   = rm.filter(m => m.result === 'W').length;
    const losses = rm.filter(m => m.result === 'L').length;
    const draws  = rm.filter(m => m.result === 'D').length;
    const played = rm.length;
    return { played, wins, losses, draws, win_pct: played > 0 ? Math.round(wins/played*100) : 0 };
  }, [rankingsEntry, profile]);

  // ── Per-sport: use rankings entry as primary source ───────────────────────
  const perSportFromMatches = React.useMemo(() => {
    if (rankingsEntry?.per_sport) {
      return Object.entries(rankingsEntry.per_sport).map(([sc, ss]) => ({
        sport_code: sc,
        played:     ss.played   || 0,
        wins:       ss.wins     || 0,
        losses:     (ss.played || 0) - (ss.wins || 0),
        draws:      0,
        win_pct:    ss.win_pct  || 0,
      })).sort((a, b) => b.wins - a.wins);
    }
    // Fallback: compute from recent_matches
    const rm = profile?.recent_matches || [];
    const bySprt = {};
    rm.forEach(m => {
      const sc = m.sport_code; if (!sc) return;
      if (!bySprt[sc]) bySprt[sc] = { sport_code: sc, played: 0, wins: 0, losses: 0, draws: 0, win_pct: 0 };
      bySprt[sc].played += 1;
      if      (m.result === 'W') bySprt[sc].wins   += 1;
      else if (m.result === 'L') bySprt[sc].losses += 1;
      else if (m.result === 'D') bySprt[sc].draws  += 1;
    });
    Object.values(bySprt).forEach(ps => {
      ps.win_pct = ps.played > 0 ? Math.round(ps.wins/ps.played*100) : 0;
    });
    return Object.values(bySprt).sort((a,b) => b.wins - a.wins);
  }, [rankingsEntry, profile]);

  // ── Tournament history from recent_matches ───────────────────────────────
  const tournamentHistory = React.useMemo(() => {
    const rm = profile?.recent_matches || [];
    const tt = profile?.tournament_teams || [];
    const byTourn = {};
    rm.forEach(m => {
      const tn = m.tournament_name || '—';
      if (!byTourn[tn]) byTourn[tn] = { played:0, wins:0, losses:0, draws:0 };
      byTourn[tn].played += 1;
      if      (m.result === 'W') byTourn[tn].wins   += 1;
      else if (m.result === 'L') byTourn[tn].losses += 1;
      else if (m.result === 'D') byTourn[tn].draws  += 1;
    });
    return tt.map(t => ({
      ...t,
      stats: byTourn[t.tournament_name || '—'] || { played:0, wins:0, losses:0, draws:0 },
    }));
  }, [profile]);

  // Sport drilldown state
  const [expandedSport, setExpandedSport] = React.useState(null);

  if (loading) return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto space-y-5">
      <Skeleton className="h-40 bg-[#1d2230] rounded-2xl" />
      <Skeleton className="h-28 bg-[#1d2230] rounded-2xl" />
      <Skeleton className="h-48 bg-[#1d2230] rounded-2xl" />
    </div>
  );

  const { player, team, teammates, stats, per_sport, upcoming, recent_matches,
          role_in_team, jersey_number, platinum_sports } = profile || {};
  const displayName = profile?.user?.display_name || player?.full_name || 'Member';
  const photo = editPhoto || player?.photo_url;
  const sports = player?.sports || [];

  // Scorer-specific: check if this user is ONLY a scorer (not a player)
  const userRoles  = user?.memberships?.[0]?.roles || [];
  const isScorerOnly = userRoles.includes('scorer') &&
                       !userRoles.some(r => ['player','org_owner','org_admin','tournament_manager'].includes(r));
  const scorerSports = profile?.scorer_sports || [];

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto pb-16" data-testid="member-profile-page">

      {/* ── Profile card ─────────────────────────────────────────── */}
      <Card className="bg-[#10131c] border-[#252a37] mb-5 overflow-hidden">
        {/* Gradient banner */}
        <div className="h-24 relative"
          style={{ background: `linear-gradient(135deg, ${team?.color || '#f5c518'}30, #1d2230)` }}>
          {/* Edit / Refresh button */}
          <div className="absolute top-3 right-3 flex gap-2">
            {!editing && (
              <button onClick={fetchProfile} className="h-8 w-8 rounded-full bg-[#1d2230]/80 flex items-center justify-center text-[#8a93a3] hover:text-[#f0f2f7] transition-colors">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Player Card button — always visible */}
            {!editing && rankingsEntry && (
              <button onClick={() => setShowCard(true)}
                className="h-8 px-3 rounded-full bg-[rgba(245,197,24,0.15)] flex items-center gap-1.5 text-xs text-[#f5c518] hover:bg-[rgba(245,197,24,0.25)] transition-colors font-semibold border border-[rgba(245,197,24,0.3)]">
                <Share2 className="h-3 w-3" /> Card
              </button>
            )}
            {!editing ? (
              <button onClick={startEdit} data-testid="edit-profile-button"
                className="h-8 px-3 rounded-full bg-[#1d2230]/80 flex items-center gap-1.5 text-xs text-[#f5c518] hover:bg-[#252a37] transition-colors font-semibold">
                <Edit2 className="h-3 w-3" /> Edit
              </button>
            ) : (
              <div className="flex gap-1.5">
                <button onClick={() => setEditing(false)}
                  className="h-8 w-8 rounded-full bg-[#1d2230]/80 flex items-center justify-center text-[#8a93a3] hover:text-[#ef4444]">
                  <X className="h-4 w-4" />
                </button>
                <button onClick={handleSave} disabled={saving} data-testid="save-profile-button"
                  className="h-8 px-3 rounded-full bg-[#22c55e] flex items-center gap-1.5 text-xs text-white font-bold disabled:opacity-60">
                  <Check className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </div>

        <CardContent className="p-5 -mt-10 relative">
          <div className="flex items-end gap-4 mb-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="h-20 w-20 rounded-2xl border-4 border-[#10131c] overflow-hidden flex items-center justify-center font-black text-2xl text-white"
                style={{ backgroundColor: team?.color || '#f5c518' }}>
                {photo
                  ? <img src={photo} className="h-full w-full object-cover" alt="" />
                  : displayName[0]?.toUpperCase()}
              </div>
              {editing && (
                <button onClick={() => photoRef.current?.click()}
                  className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#f5c518] flex items-center justify-center"
                  title="Change photo">
                  <Camera className="h-3.5 w-3.5 text-[#0b0f18]" />
                </button>
              )}
              <input type="file" accept="image/*" ref={photoRef} hidden onChange={handlePhotoChange} />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0">
              {editing ? (
                <Input value={editName} onChange={e => setEditName(e.target.value)}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] font-bold text-lg h-10 mb-1"
                  data-testid="edit-name-input" />
              ) : (
                <h1 className="text-xl font-black text-[#f0f2f7] truncate">{displayName}</h1>
              )}
              <p className="text-sm text-[#8a93a3] font-mono">{profile?.user?.phone}</p>
              {(ROLE_LABELS[role_in_team] || jersey_number) && (
                <div className="flex items-center gap-2 mt-0.5">
                  {ROLE_LABELS[role_in_team] && (
                    <Badge className="bg-[rgba(245,197,24,0.1)] text-[#f5c518] border-[rgba(245,197,24,0.3)] text-[10px]">
                      {ROLE_LABELS[role_in_team]}
                    </Badge>
                  )}
                  {jersey_number && (
                    <span className="text-xs text-[#8a93a3]">#{jersey_number}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* DOB */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-[10px] text-[#8a93a3] uppercase tracking-wider mb-1">Date of Birth</p>
              {editing ? (
                <Input type="date" value={editDob} onChange={e => setEditDob(e.target.value)}
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] h-8 text-sm"
                  data-testid="edit-dob-input" />
              ) : (
                <p className="text-sm font-semibold text-[#f0f2f7]">{fmtDate(player?.dob)}</p>
              )}
            </div>
            <div>
              <p className="text-[10px] text-[#8a93a3] uppercase tracking-wider mb-1">Sports</p>
              <div className="flex flex-wrap gap-1">
                {sports.length > 0
                  ? sports.map(s => (
                    <span key={s} className="text-sm" title={SPORT_LABELS[s] || s}>
                      <SportIcon sport={s} size={22}/>
                    </span>
                  ))
                  : <span className="text-sm text-[#8a93a3]">—</span>}
              </div>
            </div>
          </div>

          {/* Platinum sports */}
          {platinum_sports?.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[rgba(245,197,24,0.06)] border border-[rgba(245,197,24,0.2)] rounded-xl">
              <Star className="h-3.5 w-3.5 text-[#f5c518] flex-shrink-0" />
              <p className="text-xs text-[#f5c518] font-semibold">
                Platinum · {platinum_sports.map(s => SPORT_LABELS[s] || s).join(', ')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Team card ─────────────────────────────────────────────── */}
      {team && (
        <Card className="bg-[#10131c] border-[#252a37] mb-5">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center font-black text-white text-lg overflow-hidden flex-shrink-0"
                style={{ backgroundColor: team.color || '#3b82f6' }}>
                {team.logo_url?.startsWith('data:')
                  ? <img src={team.logo_url} className="h-full w-full object-cover" alt="" />
                  : team.name?.[0]}
              </div>
              <div>
                <p className="font-bold text-[#f0f2f7]">{team.name}</p>
                <p className="text-xs text-[#8a93a3]">{teammates.length + 1} players</p>
              </div>
              <Badge className="ml-auto bg-[#1d2230] border-[#252a37] text-[#8a93a3] text-xs">My Team</Badge>
            </div>

            {/* Teammates */}
            {teammates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {teammates.map(t => {
                  const name = t.full_name || `${t.first_name || ''} ${t.last_name || ''}`.trim();
                  return (
                    <div key={t.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#161a26] border border-[#252a37] rounded-xl">
                      <div className="h-6 w-6 rounded-full bg-[#252a37] flex items-center justify-center text-[10px] font-bold text-[#f5c518]">
                        {name[0]?.toUpperCase()}
                      </div>
                      <span className="text-xs text-[#f0f2f7] font-medium">{name.split(' ')[0]}</span>
                      {t.is_platinum && <Star className="h-2.5 w-2.5 text-[#f5c518]" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Scorer-only view: show assigned sports, hide player stats ──── */}
      {isScorerOnly ? (
        <Card className="bg-[#10131c] border-[#252a37] mb-5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-[#f5c518]"/>
              <h2 className="text-sm font-bold text-[#8a93a3] uppercase tracking-wider">Scorer Role</h2>
            </div>
            <p className="text-sm text-[#8a93a3] mb-3">
              You are registered as a <span className="text-[#f5c518] font-semibold">Scorer</span> — not a player.
              Contact the admin to update your assigned sports.
            </p>
            {scorerSports.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {scorerSports.map(sc => (
                  <span key={sc} className="flex items-center gap-1 text-xs bg-[rgba(245,197,24,0.1)] border border-[rgba(245,197,24,0.2)] text-[#f5c518] px-2 py-1 rounded-full font-semibold">
                    <SportIcon sport={sc} size={14}/> {SPORT_LABELS[sc] || sc}
                  </span>
                ))}
              </div>
            )}
            {scorerSports.length === 0 && (
              <p className="text-xs text-[#8a93a3] italic">No sports assigned yet. Admin can assign via Scorers page.</p>
            )}
          </CardContent>
        </Card>
      ) : (<>

      {/* ── Stats row ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3 mb-5">        {[
          { label: 'Played',  value: sportStats?.played || 0,    color: '#8a93a3' },
          { label: 'Wins',    value: sportStats?.wins || 0,      color: '#22c55e' },
          { label: 'Losses',  value: sportStats?.losses || 0,    color: '#ef4444' },
          { label: 'Win %',   value: `${sportStats?.win_pct || 0}%`, color: '#f5c518' },
        ].map(({ label, value, color }) => (
          <Card key={label} className="bg-[#10131c] border-[#252a37]">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-black tabular-nums" style={{ color }}>{value}</p>
              <p className="text-[10px] text-[#8a93a3] uppercase tracking-wider mt-0.5">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Per-sport stats ──────────────────────────────────────── */}
      {perSportFromMatches.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37] mb-5">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <Target className="h-3.5 w-3.5" /> Sport-wise Performance
            </h2>
            <div className="space-y-2">
              {perSportFromMatches.map(ps => {
                const pct = ps.win_pct || 0;
                const isOpen = expandedSport === ps.sport_code;
                const sportMatches = (recent_matches || []).filter(m => m.sport_code === ps.sport_code);
                const RESULT_COLORS = { W:'#22c55e', L:'#ef4444', D:'#f5c518', BYE:'#8a93a3' };
                return (
                  <div key={ps.sport_code} className="rounded-xl overflow-hidden border border-[#252a37]">
                    <button
                      onClick={() => setExpandedSport(isOpen ? null : ps.sport_code)}
                      className="w-full flex flex-col gap-1 p-3 hover:bg-[#161a26] transition-colors text-left">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#f0f2f7] flex items-center gap-1.5">
                          <SportIcon sport={ps.sport_code} size={16}/>
                          {SPORT_LABELS[ps.sport_code] || ps.sport_code}
                        </span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-[#22c55e] font-bold">{ps.wins}W</span>
                          <span className="text-[#8a93a3]">{ps.draws}D</span>
                          <span className="text-[#ef4444]">{ps.losses}L</span>
                          <span className="text-[#f5c518] font-black w-10 text-right">{pct}%</span>
                          <ChevronRight className={`h-3.5 w-3.5 text-[#8a93a3] transition-transform ${isOpen ? 'rotate-90' : ''}`}/>
                        </div>
                      </div>
                      <div className="h-1.5 bg-[#1d2230] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: pct >= 60 ? '#22c55e' : pct >= 40 ? '#f5c518' : '#ef4444' }} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-[#1e2435] bg-[#0a0d14]">
                        {sportMatches.length === 0 ? (
                          <p className="text-[10px] text-[#8a93a3] px-3 py-2">No individual match records for this sport</p>
                        ) : (
                          <div className="divide-y divide-[#1e2435]/60">
                            {sportMatches.map((m, i) => {
                              const rc = RESULT_COLORS[m.result] || '#8a93a3';
                              const scoreStr = m.my_score != null && m.opp_score != null ? `${m.my_score}–${m.opp_score}` : null;
                              const games = (m.games || []).filter(g => g.home || g.away);
                              return (
                                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs">
                                  <div className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center font-black text-xs border"
                                    style={{ color: rc, borderColor: `${rc}40`, backgroundColor: `${rc}10` }}>
                                    {m.result}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[#f0f2f7] font-semibold truncate">vs {m.opp_player || m.opp_team || 'Opponent'}</p>
                                    {m.opp_team && m.opp_player !== m.opp_team && (
                                      <p className="text-[10px] text-[#8a93a3] truncate">{m.opp_team}</p>
                                    )}
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    {scoreStr && <p className="font-black tabular-nums" style={{ color: rc }}>{scoreStr}</p>}
                                    {games.length > 0 && (
                                      <p className="text-[9px] text-[#8a93a3] font-mono">{games.map(g=>`${g.home}-${g.away}`).join(' ')}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Tournament History ────────────────────────────────────── */}
      {tournamentHistory.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37] mb-5">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5" /> Tournament History
            </h2>
            <div className="space-y-2">
              {tournamentHistory.map((t, i) => {
                const s = t.stats || {};
                const pct = s.played > 0 ? Math.round(s.wins / s.played * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-3 bg-[#161a26] border border-[#252a37] rounded-xl">
                    <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-xs text-white overflow-hidden"
                      style={{ backgroundColor: t.team_color || '#3b82f6' }}>
                      {t.team_logo?.startsWith('data:')
                        ? <img src={t.team_logo} className="h-full w-full object-cover" alt=""/>
                        : (t.team_name?.[0] || '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#f0f2f7] truncate">{t.tournament_name || '—'}</p>
                      <p className="text-xs text-[#8a93a3] truncate">{t.team_name}</p>
                    </div>
                    {s.played > 0 ? (
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-black" style={{ color: pct >= 50 ? '#22c55e' : '#ef4444' }}>
                          {s.wins}W · {s.losses}L
                        </p>
                        <p className="text-[10px] text-[#8a93a3]">{pct}% · {s.played}P</p>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#8a93a3]">No matches recorded</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Upcoming schedule ────────────────────────────────────── */}
      {upcoming?.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37] mb-5">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5" /> Upcoming Matches
            </h2>
            <div className="space-y-2">
              {upcoming.map((enc, i) => (
                <div key={enc.id || i}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                    enc.status === 'in_progress'
                      ? 'bg-[rgba(239,68,68,0.05)] border-[rgba(239,68,68,0.3)]'
                      : 'bg-[#161a26] border-[#252a37]'
                  }`}>
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center font-black text-white text-sm flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: enc.opponent?.color || '#3b82f6' }}>
                    {enc.opponent?.logo_url?.startsWith('data:')
                      ? <img src={enc.opponent.logo_url} className="h-full w-full object-cover" alt="" />
                      : (enc.opponent?.name || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#f0f2f7] truncate">
                      vs {enc.opponent?.name || enc.away_seed || 'TBD'}
                    </p>
                    <p className="text-xs text-[#8a93a3] flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {fmtTime(enc.scheduled_at)}
                    </p>
                  </div>
                  {enc.status === 'in_progress' && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#ef4444]">
                      <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse" />LIVE
                    </span>
                  )}
                  {enc.match_label && (
                    <span className="text-[10px] font-mono text-[#8a93a3] flex-shrink-0">{enc.match_label}</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Recent history ───────────────────────────────────────── */}
      {recent_matches?.length > 0 && (
        <Card className="bg-[#10131c] border-[#252a37]">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#8a93a3] uppercase tracking-wider mb-3 flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5" /> Recent Matches
            </h2>
            <div className="space-y-2">
              {recent_matches.map((m, i) => {
                const colors = { W: '#22c55e', L: '#ef4444', D: '#f5c518', BYE: '#8a93a3' };
                const rc = colors[m.result] || '#8a93a3';
                const isResultSport = ['chess','carrom'].includes(m.sport_code);
                const scoreStr = m.my_score != null && m.opp_score != null
                  ? `${m.my_score}–${m.opp_score}`
                  : null;
                const games = (m.games || []).filter(g => g.home || g.away);
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-[#161a26] border border-[#252a37] rounded-xl">
                    {/* Result badge */}
                    <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-base border-2"
                      style={{ color: rc, borderColor: `${rc}40`, backgroundColor: `${rc}10` }}>
                      {m.result}
                    </div>
                    {/* Sport icon */}
                    <div className="flex-shrink-0 text-center">
                      <SportIcon sport={m.sport_code} size={20}/>
                      {m.slot && <p className="text-[8px] text-[#8a93a3] font-mono mt-0.5">{m.slot}</p>}
                    </div>
                    {/* Opponent info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#f0f2f7] truncate">
                        vs {m.opp_player || m.opp_team || 'Opponent'}
                      </p>
                      <p className="text-xs text-[#8a93a3] truncate">
                        {m.opp_team && m.opp_player !== m.opp_team ? m.opp_team : m.tournament_name || ''}
                      </p>
                    </div>
                    {/* Score */}
                    <div className="text-right flex-shrink-0">
                      {isResultSport ? (
                        <p className="text-sm font-black"
                          style={{ color: rc }}>
                          {m.result === 'W' ? 'Win' : m.result === 'L' ? 'Loss' : 'Draw'}
                        </p>
                      ) : scoreStr ? (
                        <>
                          <p className="text-sm font-black tabular-nums" style={{ color: rc }}>{scoreStr}</p>
                          {games.length > 0 && (
                            <p className="text-[9px] text-[#8a93a3] font-mono">
                              {games.map(g => `${g.home}-${g.away}`).join(' ')}
                            </p>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!team && !loading && (
        <Card className="bg-[#10131c] border-[#252a37]">
          <CardContent className="p-10 text-center">
            <Users className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
            <p className="text-[#f0f2f7] font-semibold">Not assigned to a team yet</p>
            <p className="text-sm text-[#8a93a3] mt-1">Contact the tournament admin to get assigned</p>
          </CardContent>
        </Card>
      )}

      {/* Close scorer-only conditional */}
      </>)}

      {/* ── Player Card Modal (same as Rankings) ── */}
      {showCard && rankingsEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowCard(false)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-sm">
            <div className="relative rounded-3xl overflow-hidden bg-[#0b0f18] border-2 border-[rgba(245,197,24,0.4)] shadow-[0_0_40px_rgba(245,197,24,0.15)]">
              <div className="h-1.5 w-full bg-gradient-to-r from-[#f5c518] via-[#f7d24a] to-[#f5c518]"/>
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-2xl flex-shrink-0 flex items-center justify-center font-black text-3xl text-white overflow-hidden border-2 border-[rgba(245,197,24,0.3)]"
                    style={{ backgroundColor: team?.color || '#3b82f6' }}>
                    {photo
                      ? <img src={photo} className="h-full w-full object-cover" alt=""/>
                      : displayName?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xl font-black text-[#f0f2f7] leading-tight">{displayName}</p>
                    {team && <p className="text-sm text-[#8a93a3] mt-0.5 truncate">{team.name}</p>}
                    {rankingsEntry.is_platinum && (
                      <span className="text-[10px] font-black text-[#f5c518] bg-[rgba(245,197,24,0.15)] px-2 py-0.5 rounded-full border border-[rgba(245,197,24,0.3)] inline-block mt-1">★ Platinum</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 px-5 pb-4">
                {[
                  { label:'Played', value: rankingsEntry.played,  color:'#f0f2f7' },
                  { label:'Wins',   value: rankingsEntry.wins,    color:'#22c55e' },
                  { label:'Win%',   value:`${rankingsEntry.win_pct}%`, color:'#f5c518' },
                ].map(s => (
                  <div key={s.label} className="bg-[#161a26] rounded-2xl py-3 text-center border border-[#252a37]">
                    <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-[10px] text-[#8a93a3] uppercase tracking-wider mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {Object.keys(rankingsEntry.per_sport || {}).length > 0 && (
                <div className="px-5 pb-4">
                  <p className="text-[10px] text-[#8a93a3] uppercase tracking-widest font-bold mb-2">Sport Stats</p>
                  <div className="space-y-1.5">
                    {Object.entries(rankingsEntry.per_sport).sort((a,b)=>b[1].wins-a[1].wins).map(([sc, ss]) => {
                      const pct = ss.win_pct || (ss.played > 0 ? Math.round(ss.wins/ss.played*100) : 0);
                      return (
                        <div key={sc} className="flex items-center gap-2.5 bg-[#161a26] rounded-xl px-3 py-2 border border-[#252a37]">
                          <span className="text-lg"><SportIcon sport={sc} size={28}/></span>
                          <span className="text-sm font-semibold text-[#f0f2f7] flex-1">{SPORT_LABELS[sc]||sc}</span>
                          <span className="text-sm font-black text-[#22c55e]">{ss.wins}W</span>
                          <span className="text-xs text-[#8a93a3]">{ss.played}P</span>
                          <span className="text-xs font-bold w-10 text-right" style={{ color: pct>=60?'#22c55e':pct>=40?'#f5c518':'#ef4444' }}>{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="px-5 pb-4 flex items-center justify-between">
                <p className="text-[10px] text-[#8a93a3]/50 font-mono">BTCCP · Circles Sports League</p>
                <Trophy className="h-4 w-4 text-[#f5c518]/40"/>
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => {
                  const url = `${window.location.origin}/player/${rankingsEntry.player_id}`;
                  navigator.clipboard.writeText(url).then(() => toast.success('Link copied!')).catch(()=>{});
                }}
                className="flex-1 h-11 rounded-xl bg-[rgba(245,197,24,0.1)] border border-[rgba(245,197,24,0.3)] text-[#f5c518] text-sm font-bold flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4"/>Copy Link
              </button>
              <button onClick={() => setShowCard(false)}
                className="h-11 w-11 rounded-xl bg-[#1d2230] border border-[#252a37] text-[#8a93a3] flex items-center justify-center">
                <X className="h-4 w-4"/>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
